// packages/app-shell/src/process/StepEditMenu.tsx
//
// The WYSIWYG step affordance shared by every process surface (tree spine,
// flow/sequence cells, BPMN nodes). A ⋯ trigger opens the canonical @archlang/ui
// `Menu` (floating-ui: portal to <body>, Esc / click-outside close, arrow-key
// nav, danger styling — DESIGN-SYSTEM.md §5) whose items map 1:1 to the shared
// ProcessStepEditApi: wrap-in-construct, add/insert, delete, retype-callee,
// reassign-owner. Text-carrying actions swap the menu to an inline input whose
// value is passed verbatim to the api (chip→rowText zero-drift; the recompile
// invariant is the last guard). Structure-only, no model state.

import React, { useState } from "react";
import { Button, Menu, MenuButton, MenuItems, MenuItem } from "@archlang/ui";
import type { NodeId } from "@archlang/engine";
import type { ProcessStepEditApi, WrapConstruct } from "./processStepEdit.js";
import { WRAP_CONSTRUCTS } from "./processConstructs.js";
import { InlineEditInput } from "./InlineEditInput.js";
import {
  addableDescriptors,
  presentChips,
  type StepModifierDescriptor,
  type StepModifierKind,
  type StepModifierValues,
} from "./stepModifierChips.js";

export interface StepEditMenuProps {
  readonly api: ProcessStepEditApi;
  readonly nodeId: NodeId;
  /** `call` unlocks retype-callee + reassign-owner; `block` unlocks
   *  add-step-inside; other nodes get add/delete/wrap only. */
  readonly kind: "call" | "block" | "other";
  /** Walk stepIndex — enables the owner-reassign item when reassignable. */
  readonly stepIndex?: number;
  /** For a `block` node, the branching construct — unlocks "Add else" (`if`) /
   *  "Add catch" (`try`). Sibling-arm inserts (plan §170). */
  readonly blockConstruct?: "if" | "try";
  /** Whether an `if` can still take an `else` (none authored yet) — gates the
   *  "Add else" item (capability = presence). */
  readonly canAddElse?: boolean;
  /** For a `block` node: whether it can be flattened in place (a single-body
   *  construct — not an `if`+else / `select` / `try`+catch / multi-branch
   *  `parallel`). Gates the "Unwrap" item (capability = presence). */
  readonly canUnwrap?: boolean;
  /** For a `block` node: the constructs it can cleanly morph INTO (e.g. a
   *  single-body `parallel` → `["each"]`). Each renders a "Morph to X" item;
   *  empty/absent ⇒ no morph offered. */
  readonly morphTargets?: readonly WrapConstruct[];
  /** For a `parallel` block: offer the merge-behaviour items (`join`/`race`/
   *  `out`/plain + set-count). Present only on a plain parallel (not an await
   *  boundary / select form). `current` prefills the count prompt. */
  readonly merge?: { readonly canSet: boolean; readonly current?: string };
  /** For a `select` (`switch`) or `parallel select` block: offer the inclusive
   *  run-count items (`all`/`one`/N). `parallel` routes to the right op;
   *  `runCount` prefills the "Set N" prompt. */
  readonly selectCount?: { readonly parallel: boolean; readonly runCount?: number };
  /** For a `parallel await` boundary: offer an inline-edit of the awaited head. */
  readonly awaitBoundary?: boolean;
  /** For a `call` node: the step's current trailing modifiers (`as` label +
   *  freehand comment). Drives the modifier-chips section — a present modifier is
   *  an editable/removable chip, an absent one a "+ add" affordance (P3 #24). */
  readonly modifiers?: { readonly asName?: string; readonly comment?: string };
  /** Prefill for the retype-callee input (`from > to`). */
  readonly from?: string;
  readonly to?: string;
  /** Extra classes on the trigger (positioning per surface). */
  readonly className?: string;
}

type Prompt = { readonly label: string; readonly initial: string; readonly run: (v: string) => boolean };

/** The step-edit affordance. Renders nothing when the api is read-only. */
export function StepEditMenu({ api, nodeId, kind, stepIndex, blockConstruct, canAddElse, canUnwrap, morphTargets, merge, selectCount, awaitBoundary, modifiers, from, to, className }: StepEditMenuProps): React.JSX.Element | null {
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  // Menu is uncontrolled; bump the key to remount it CLOSED after a committed
  // action (the shared component owns open/dismiss otherwise).
  const [nonce, setNonce] = useState(0);
  if (!api.canEdit) return null;

  const close = () => {
    setPrompt(null);
    setNonce((n) => n + 1);
  };
  const act = (ok: boolean) => {
    if (ok) close();
  };
  const canReassign = kind === "call" && stepIndex !== undefined && api.canReassignOwner(stepIndex);

  return (
    // Stop the surface's row-level click (select/dive) from firing through the
    // affordance; the Menu handles its own open/close.
    <span className={"inline-flex " + (className ?? "")} onClick={(e) => e.stopPropagation()}>
      <Menu key={nonce} placement="bottom-start">
        <MenuButton
          aria-label="Edit step"
          data-step-edit-trigger=""
          className="rounded-sm px-1 text-xs leading-none text-slate-500 hover:bg-slate-700/50 hover:text-slate-200"
        >
          ⋯
        </MenuButton>
        <MenuItems className="au-panel-in z-overlay min-w-[9rem] overflow-hidden rounded-md border border-slate-700 bg-slate-900/95 py-1 text-xs shadow-xl shadow-black/40 backdrop-blur outline-none">
          <div data-step-edit-menu="">
            {prompt ? (
              <InlinePrompt
                label={prompt.label}
                initial={prompt.initial}
                onCancel={() => setPrompt(null)}
                onSubmit={(v) => act(prompt.run(v))}
              />
            ) : (
              <>
                <MenuItem
                  keepOpen
                  onClick={() => setPrompt({ label: "Step (e.g. A > B)", initial: "", run: (v) => api.addStepAfter(nodeId, v) })}
                >
                  Add step after
                </MenuItem>
                <MenuItem
                  keepOpen
                  onClick={() => setPrompt({ label: "Step (e.g. A > B)", initial: "", run: (v) => api.insertBefore(nodeId, v) })}
                >
                  Add step before
                </MenuItem>
                {kind === "block" && (
                  <MenuItem
                    keepOpen
                    onClick={() => setPrompt({ label: "Step (e.g. A > B)", initial: "", run: (v) => api.insertInto(nodeId, v) })}
                  >
                    Add step inside
                  </MenuItem>
                )}
                {blockConstruct === "if" && canAddElse && (
                  <MenuItem onClick={() => act(api.addElse(nodeId))}>Add else</MenuItem>
                )}
                {blockConstruct === "try" && (
                  <MenuItem onClick={() => act(api.addCatch(nodeId))}>Add catch</MenuItem>
                )}
                {kind === "block" && canUnwrap && (
                  <MenuItem data-unwrap="" onClick={() => act(api.unwrapBlock(nodeId))}>
                    Unwrap
                  </MenuItem>
                )}
                {kind === "block" &&
                  (morphTargets ?? []).map((t) => (
                    <MenuItem key={t} data-morph={t} onClick={() => act(api.morphBlock(nodeId, t))}>
                      Morph to {t}
                    </MenuItem>
                  ))}
                {merge?.canSet && (
                  <>
                    <div className="my-1 border-t border-slate-800" />
                    <div className="px-2 pb-0.5 pt-1 text-[10px] uppercase tracking-wide text-slate-500">Merge</div>
                    <div className="flex flex-wrap gap-1 px-2 pb-1">
                      {(["join", "race", "out", "plain"] as const).map((p) => (
                        <Button
                          key={p}
                          variant="ghost"
                          size="xs"
                          data-merge={p}
                          onClick={() =>
                            act(
                              api.setMergeClause(
                                nodeId,
                                p === "out" ? { out: true } : p === "plain" ? { out: false } : { policy: p },
                              ),
                            )
                          }
                        >
                          {p}
                        </Button>
                      ))}
                    </div>
                    <MenuItem
                      keepOpen
                      onClick={() =>
                        setPrompt({
                          label: "Threshold N (join/race)",
                          initial: (merge.current?.match(/\d+/) ?? [""])[0],
                          run: (v) => {
                            const n = Number(v.trim());
                            // A count only means anything with a join/race policy; keep the
                            // rendered one when it is join/race, else default to join.
                            const policy = merge.current?.includes("race") ? "race" : "join";
                            return Number.isInteger(n) && n >= 1 && api.setMergeClause(nodeId, { policy, count: n });
                          },
                        })
                      }
                    >
                      Set threshold…
                    </MenuItem>
                  </>
                )}
                {selectCount && (
                  <>
                    <div className="my-1 border-t border-slate-800" />
                    <div className="px-2 pb-0.5 pt-1 text-[10px] uppercase tracking-wide text-slate-500">Runs</div>
                    <div className="flex flex-wrap gap-1 px-2 pb-1">
                      {(["all", "one"] as const).map((c) => (
                        <Button
                          key={c}
                          variant="ghost"
                          size="xs"
                          data-select-count={c}
                          onClick={() =>
                            act(
                              selectCount.parallel
                                ? api.setParallelSelectCount(nodeId, c)
                                : api.setSelectCount(nodeId, c),
                            )
                          }
                        >
                          {c}
                        </Button>
                      ))}
                    </div>
                    <MenuItem
                      keepOpen
                      onClick={() =>
                        setPrompt({
                          label: "Run first N",
                          initial: selectCount.runCount !== undefined ? String(selectCount.runCount) : "",
                          run: (v) => {
                            const n = Number(v.trim());
                            if (!Number.isInteger(n) || n < 1) return false;
                            return selectCount.parallel ? api.setParallelSelectCount(nodeId, n) : api.setSelectCount(nodeId, n);
                          },
                        })
                      }
                    >
                      Run first N…
                    </MenuItem>
                  </>
                )}
                {awaitBoundary && (
                  <MenuItem
                    keepOpen
                    onClick={() => setPrompt({ label: "Await head (e.g. 1 day)", initial: "", run: (v) => api.setAwaitTarget(nodeId, v.trim()) })}
                  >
                    Edit await…
                  </MenuItem>
                )}
                {kind === "call" && (
                  <ModifierChips
                    values={modifiers ?? {}}
                    onSet={(k, text) => act(api.setStepModifier(nodeId, k, text))}
                    onRemove={(k) => act(api.setStepModifier(nodeId, k, ""))}
                  />
                )}
                {kind === "call" && (
                  <MenuItem
                    keepOpen
                    onClick={() =>
                      setPrompt({
                        label: "from > to",
                        initial: from && to ? `${from} > ${to}` : "",
                        run: (v) => {
                          const [f, t] = v.split(">").map((s) => s.trim());
                          return f !== undefined && t !== undefined && api.replaceCall(nodeId, f, t);
                        },
                      })
                    }
                  >
                    Change callee…
                  </MenuItem>
                )}
                {canReassign && (
                  <MenuItem
                    keepOpen
                    onClick={() =>
                      setPrompt({ label: "New owner module", initial: from ?? "", run: (v) => api.reassignOwner(stepIndex!, v.trim()) })
                    }
                  >
                    Reassign owner…
                  </MenuItem>
                )}
                <div className="my-1 border-t border-slate-800" />
                <div className="px-2 pb-0.5 pt-1 text-[10px] uppercase tracking-wide text-slate-500">Wrap in</div>
                <div className="flex flex-wrap gap-1 px-2 pb-1">
                  {WRAP_CONSTRUCTS.map((c) => (
                    <Button
                      key={c}
                      variant="ghost"
                      size="xs"
                      data-wrap={c}
                      onClick={() => act(api.wrap(nodeId, c))}
                    >
                      {c}
                    </Button>
                  ))}
                </div>
                <div className="my-1 border-t border-slate-800" />
                <MenuItem danger keepOpen onClick={() => act(api.deleteStep(nodeId))}>
                  Delete
                </MenuItem>
              </>
            )}
          </div>
        </MenuItems>
      </Menu>
    </span>
  );
}

/** The modifier-chips section of a call's edit menu (P3 #24). A present modifier
 *  (`as <label>` / `"comment"`) renders as a chip: click the body to edit inline
 *  (Enter → setStepModifier; empty removes), × to remove. An absent modifier is a
 *  "+ label" affordance opening the same inline input. Capability = presence, so
 *  the chip/add split comes straight from `stepModifierChips`. Renders nothing
 *  when the step carries no modifiers and none are addable (never, for a call). */
function ModifierChips({
  values,
  onSet,
  onRemove,
}: {
  readonly values: StepModifierValues;
  /** Commit a value for a modifier kind (add or replace). */
  readonly onSet: (kind: StepModifierKind, text: string) => void;
  /** Remove a modifier kind. */
  readonly onRemove: (kind: StepModifierKind) => void;
}): React.JSX.Element | null {
  // Which kind (if any) is currently being edited inline; null = the chip row.
  const [editing, setEditing] = useState<StepModifierKind | null>(null);
  const chips = presentChips(values);
  const addable = addableDescriptors(values);
  if (chips.length === 0 && addable.length === 0) return null;

  const editorFor = (d: StepModifierDescriptor, initial: string) => (
    <div className="px-2 pb-1" onClick={(e) => e.stopPropagation()}>
      <InlineEditInput
        initial={initial}
        placeholder={d.placeholder}
        ariaLabel={`Edit ${d.label}`}
        onCommit={(v) => {
          onSet(d.kind, v.trim());
          setEditing(null);
        }}
        onCancel={() => setEditing(null)}
        className="w-40 box-border rounded-sm border border-sky-500 bg-slate-950 px-1.5 py-0.5 text-[11px] text-slate-100 outline-none"
      />
    </div>
  );

  const editingChip = editing !== null ? chips.find((c) => c.kind === editing) : undefined;
  const editingAdd = editing !== null && !editingChip ? addable.find((d) => d.kind === editing) : undefined;

  return (
    <>
      <div className="my-1 border-t border-slate-800" />
      <div className="px-2 pb-0.5 pt-1 text-[10px] uppercase tracking-wide text-slate-500">Modifiers</div>
      {editingChip
        ? editorFor(editingChip.descriptor, editingChip.value)
        : editingAdd
          ? editorFor(editingAdd, "")
          : (
            <div className="flex flex-wrap gap-1 px-2 pb-1">
              {chips.map((c) => (
                <span
                  key={c.kind}
                  data-modifier-chip={c.kind}
                  className="inline-flex items-center gap-1 rounded-sm border border-slate-700 bg-slate-800/50 pl-1.5 text-[11px] text-slate-200"
                >
                  <button
                    type="button"
                    data-modifier-edit={c.kind}
                    title={`Edit ${c.descriptor.label}`}
                    className="py-0.5 hover:text-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                    onClick={() => setEditing(c.kind)}
                  >
                    {c.descriptor.render(c.value)}
                  </button>
                  <button
                    type="button"
                    data-modifier-remove={c.kind}
                    aria-label={`Remove ${c.descriptor.label}`}
                    title={`Remove ${c.descriptor.label}`}
                    className="px-1 py-0.5 text-slate-500 hover:text-rose-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                    onClick={() => onRemove(c.kind)}
                  >
                    ×
                  </button>
                </span>
              ))}
              {addable.map((d) => (
                <Button
                  key={d.kind}
                  variant="ghost"
                  size="xs"
                  data-modifier-add={d.kind}
                  title={`Add ${d.label}`}
                  className="border border-dashed border-slate-700 hover:border-sky-500"
                  onClick={() => setEditing(d.kind)}
                >
                  + {d.label}
                </Button>
              ))}
            </div>
          )}
    </>
  );
}

function InlinePrompt({
  label,
  initial,
  onSubmit,
  onCancel,
}: {
  label: string;
  initial: string;
  onSubmit: (v: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <form
      className="px-2 py-1"
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim() !== "") onSubmit(value);
      }}
    >
      <input
        autoFocus
        value={value}
        placeholder={label}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onCancel();
          }
        }}
        className="w-40 rounded-sm border border-slate-700 bg-slate-950 px-1.5 py-0.5 text-xs text-slate-100 outline-none focus:border-sky-500"
      />
    </form>
  );
}
